import { FormattedMessage, useIntl } from 'react-intl';
import { useGetCurrentOrganization } from '@/api/trafikportalen/trafikportalenAPI';
import { cn } from '@/helpers/classnames';
import { Skeleton } from './Skeleton';
import { useDepartmentCreditInfo } from '@/hooks/useDepartmentCreditInfo';
import { CreditInfoBar, CreditInfoItem } from './CreditInfoBar';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { useMemo } from 'react';

export const DepartmentCreditInfo = ({
  departmentId,
  className,
}: {
  departmentId: string;
  className?: string;
}) => {
  const { formatMessage, locale } = useIntl();
  const { data: organization } = useGetCurrentOrganization();
  const { remainingCredit, hasNoCreditLimit } = useOrgCreditInfo();
  const { allpayCredit, cardCredit, isLoading, totalSpent } =
    useDepartmentCreditInfo(departmentId);

  const { allpayPercentage, cardPercentage, remainingPercentage } =
    useMemo(() => {
      if (!organization || isLoading)
        return {
          allpayPercentage: 0,
          cardPercentage: 0,
          remainingPercentage: 0,
        };

      if (organization.credit === 0)
        return {
          allpayPercentage: 0,
          cardPercentage: 0,
          remainingPercentage: 0,
        };

      if (totalSpent === 0)
        return {
          allpayPercentage: 0,
          cardPercentage: 0,
          remainingPercentage: 0,
        };

      if (organization.credit === undefined)
        return {
          allpayPercentage: (allpayCredit / totalSpent) * 100,
          cardPercentage: (cardCredit / totalSpent) * 100,
          remainingPercentage: (remainingCredit / totalSpent) * 100,
        };

      return {
        allpayPercentage: (allpayCredit / organization.credit) * 100,
        cardPercentage: (cardCredit / organization.credit) * 100,
        remainingPercentage: (remainingCredit / organization.credit) * 100,
      };
    }, [
      allpayCredit,
      cardCredit,
      isLoading,
      organization,
      remainingCredit,
      totalSpent,
    ]);

  if (!organization || isLoading) return <Skeleton />;

  const barItems: CreditInfoItem[] = [
    {
      label: formatMessage({
        defaultMessage: 'Tjänsteresor i app (denna avdelning)',
      }),
      bgColor: 'bg-allpay',
      color: 'text-contrast-blue',
      value: allpayCredit,
      percentage: allpayPercentage,
    },
    {
      label: formatMessage({
        defaultMessage: 'Tjänsteresor på kort (denna avdelning)',
      }),
      bgColor: 'bg-smartcard',
      color: 'text-contrast-yellow',
      value: cardCredit,
      percentage: cardPercentage,
    },
  ];

  if (!hasNoCreditLimit) {
    barItems.push({
      label: formatMessage({ defaultMessage: 'Outnyttjat (organisation)' }),
      bgColor: 'bg-background-300',
      color: 'text-text-700',
      value: remainingCredit,
      percentage: remainingPercentage,
    });
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <h3>
        <FormattedMessage defaultMessage="Kredit och spendering" />
      </h3>

      {!hasNoCreditLimit && (
        <p className="text-text-700">
          <FormattedMessage
            defaultMessage="Organisationens kreditgräns: {orgCredit} SEK"
            values={{ orgCredit: organization.credit?.toLocaleString(locale) }}
          />
        </p>
      )}

      <CreditInfoBar items={barItems} hideBar={totalSpent === 0} />
    </div>
  );
};
